import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const parseFilters = (raw) => {
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const useOrdersQueryState = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [search, setSearch] = useState(initialParams.get('search') ?? '');
    const [page, setPage] = useState(initialParams.get('page') ? Number(initialParams.get('page')) : undefined);
    const [pageSize, setPageSize] = useState(initialParams.get('pageSize') ? Number(initialParams.get('pageSize')) : undefined);
    const [sortBy, setSortBy] = useState(initialParams.get('sortBy') ?? undefined);
    const [sortOrder, setSortOrder] = useState(initialParams.get('sortOrder') ?? undefined);
    const [filters, setFilters] = useState(parseFilters(initialParams.get('filter')));

    const searchRef = useRef(null);

    const syncUrl = useCallback((params) => {
        navigate(`?${params.toString()}`, { replace: true });
    }, [navigate]);

    const buildParams = useCallback(() => {
        const params = new URLSearchParams(location.search);

        const setParam = (key, value) => {
            if (value !== undefined && value !== null && value !== '') {
                params.set(key, String(value));
            } else {
                params.delete(key);
            }
        };

        setParam('page', page);
        setParam('pageSize', pageSize);
        setParam('search', search);
        setParam('sortBy', sortBy);
        setParam('sortOrder', sortOrder);

        if (filters.length > 0) {
            params.set('filter', JSON.stringify(filters));
        } else {
            params.delete('filter');
        }

        return params;
    }, [location.search, page, pageSize, search, sortBy, sortOrder, filters]);

    const updateFilter = (property, operation, value) => {
        const updatedFilters = [...filters];
        const index = updatedFilters.findIndex((filter) => filter.property === property);

        if (!value) {
            setFilters(updatedFilters.filter((filter) => filter.property !== property));
            setPage(0);
            return;
        }

        if (index >= 0) {
            updatedFilters[index] = { property, operation, value };
        } else {
            updatedFilters.push({ property, operation, value });
        }

        setFilters(updatedFilters);
        setPage(0);
    };

    const clearFilters = () => {
        setSearch('');
        setPage(0);
        setSortBy(undefined);
        setSortOrder(undefined);
        setFilters([]);
        if (searchRef.current) {
            searchRef.current.value = '';
        }

        const params = new URLSearchParams(location.search);
        ['search', 'filter', 'page', 'sortBy', 'sortOrder'].forEach((key) => params.delete(key));
        syncUrl(params);
    };

    return {
        search,
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        filters,
        searchRef,
        buildParams,
        syncUrl,
        updateFilter,
        clearFilters,
    };
};
